import { Editor, JSONContent, getMarksBetween, getNodeAttributes } from '@tiptap/core';
import cloneDeep from 'lodash.clonedeep';
import { Mark, Slice } from 'prosemirror-model';
import { AllSelection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { WidgetType } from 'types/widget.types';

import {
  convertStyleToProseMirrorMark,
  getNearestMarksFromSelection,
  isTextStyleMark,
} from '../common/TextBasedWidgets.helpers';
import { TextTagStyleConfig, TextWidgetTag } from '../common/TextBasedWidgets.types';
import { DefaultTextType, FontStyleFromTextMarks, TextWidgetData } from './TextWidget.types';
import { VERSION } from './TextWidget.upgrade';
import {
  DEFAULT_EFFECT,
  DEFAULT_LETTERSPACING,
  DEFAULT_LINEHEIGHT,
  DEFAULT_TEXT,
  DEFAULT_TEXT_WIDGET_DATA,
} from './defaultTextData';
import { Level } from './extensions/customHeader';

/**
 * Return minimum default text data
 *
 * @returns  default text widget data
 */
export const generateDefaultData = (
  textType: DefaultTextType = 'paragraph',
  text?: string,
  hasLink?: boolean,
  options?: Partial<TextWidgetData>,
  customStyle?: Partial<TextTagStyleConfig>,
) => {
  const defaultWidgetData = DEFAULT_TEXT_WIDGET_DATA[textType];
  const {
    dimension,
    proseMirrorData: { attrs },
    style,
  } = defaultWidgetData;

  const marks = convertStyleToProseMirrorMark({ ...style, ...customStyle }, hasLink);
  const fontSize = (customStyle?.fontSize || style.fontSize) + 'px';
  const pmAttrs = {
    ...attrs,
    lineHeight: customStyle?.lineHeight || attrs.lineHeight,
    fontSize,
  };

  const proseMirrorData: JSONContent = {
    type: 'doc',
    content: generatePMParagraphContent(text || DEFAULT_TEXT, marks, pmAttrs),
  };

  const defaultData = {
    widgetType: WidgetType.Text,
    widgetData: {
      version: VERSION,
      topPx: 10,
      leftPx: 20,
      widthPx: dimension.width,
      heightPx: dimension.height,
      rotateDeg: 0,
      textTag: textType as TextWidgetTag,
      proseMirrorData,
      effect: DEFAULT_EFFECT,
      letterSpacing: DEFAULT_LETTERSPACING,
      ...options,
    },
  };

  return defaultData;
};

/**
 * Generates ProseMirror content for type 'paragraph' with the specified text content.
 * Will separate the text into separate paragraph nodes based on the newline character.
 *
 * @param text
 * @param marks
 * @param attrs
 * @param hasLink
 * @returns
 */
export const generatePMParagraphContent = (
  text: string,
  marks: JSONContent['marks'],
  attrs: { lineHeight: string; textAlign: string },
) => {
  const textContent = text || DEFAULT_TEXT;
  const paragraphs = textContent.split('\n');
  return paragraphs.map((paragraph) => ({
    type: 'paragraph',
    attrs,
    // Only include content for non-empty lines
    ...(!!paragraph && {
      content: [
        {
          type: 'text',
          text: paragraph,
          marks,
        },
      ],
    }),
  }));
};

/**
 * Returns the text marks array that stored the font size, style, and color of the first character in proseMirrorData.
 * Used for text data parsing in color contrast checker.
 *
 * @param proseMirrorData {JSONContent}
 * @returns first character's marks array
 */
export const getFirstCharacterTextMarks = (proseMirrorData: JSONContent): JSONContent['marks'] => {
  // Recursive function to get the first content
  const getFirstContent = (content: JSONContent): JSONContent => {
    if (content?.content && content?.content.length) {
      return getFirstContent(content.content[0]);
    }
    return content;
  };

  const firstContent = getFirstContent(proseMirrorData);
  if (firstContent?.marks) {
    return firstContent.marks;
  }
};

/**
 * Returns the font color, size and isBold using the textMarks array
 * Used for text data parsing in color contrast checker.
 *
 * @param textMarks {JSONContent['marks']}
 * @returns object that has color, fontSize and isBold
 */
export const getFontStyleFromTextMarks = (textMarks: JSONContent['marks']): FontStyleFromTextMarks => {
  const textStyle = textMarks?.find((mark) => mark.type === 'textStyle');
  const fontColor = textStyle?.attrs?.color ?? null;
  const fontSize = textStyle?.attrs?.fontSize ?? null;
  const fontFamily = textStyle?.attrs?.fontFamily ?? null;
  const isBold = !!textMarks?.find((mark) => mark.type === 'bold');
  const isItalic = !!textMarks?.find((mark) => mark.type === 'italic');
  const isUnderline = !!textMarks?.find((mark) => mark.type === 'underline');

  return {
    fontColor,
    fontSize,
    isBold,
    isItalic,
    isUnderline,
    fontFamily,
  };
};

// Updates font color of entire text widget
export const updateFontColor = (proseMirrorData: JSONContent, color: string, inPlace?: boolean): JSONContent => {
  const newPmData = inPlace ? proseMirrorData : cloneDeep(proseMirrorData);
  newPmData.content?.forEach((content) => {
    if (content.attrs && 'color' in content.attrs) {
      content.attrs.color = color;
    }

    const marks = content.marks;
    if (marks) {
      const textStyleIdx = marks.findIndex((mark) => mark.type === 'textStyle');
      if (textStyleIdx > -1) {
        const textStyleMarks = marks[textStyleIdx];
        if (textStyleMarks.attrs) {
          textStyleMarks.attrs.color = color;
        }
      }
    }

    if (content.content) {
      updateFontColor(content, color, true);
    }
  });

  return newPmData;
};

/**
 * Iterates through the passed data structure to replace any marks with the given ones
 *
 * @param slice Prosemirror slice to replace marks
 * @param marks Styles for overriding
 * @returns node with replaced marks
 */
export const replaceMarks = (slice: Slice, marks: readonly Mark[]): Slice => {
  slice?.content.descendants((child) => {
    if (child.isText) {
      /**
       * TODO: preserve previous assignment logic
       * Note: child.marks is readonly, it needs to fix with proper mutation method
       */
      // @ts-ignore
      child.marks = Mark.setFrom(marks);
    }
  });

  return slice;
};

/**
 * Iterates through the slice to replaces the line height attribute for a pasted slice
 * (The pasted slice format (e.g. in px/em/%) can differ from the current editor format of unitless line height (e.g. 1.2))
 * Can be extended to other attributes if needed in the future
 *
 * @param slice Prosemirror slice
 * @param attributes Attributes for overriding
 * @returns slice with replaced attributes
 */
const replaceLineHeight = (slice: Slice, lineHeight: string): Slice => {
  slice?.content.descendants((child) => {
    if (child.attrs && child.attrs.lineHeight) {
      // force override the attributes
      // @ts-ignore
      child.attrs = { ...child.attrs, lineHeight };
    }
  });

  return slice;
};

/**
 * Handles the paste event in the prosemirror editor
 * @param view current editor view
 * @param slice section of data being pasted
 */
export const handlePaste = (view: EditorView, slice: Slice, tag: TextWidgetTag) => {
  const { state, dispatch } = view;
  const { tr } = state;

  let marks: readonly Mark[];
  const nodeAttributes = getNodeAttributes(state, 'paragraph');
  const lineHeight = nodeAttributes?.lineHeight;

  // if current text content is empty, use stored marks
  if (!state.doc.textContent && state.storedMarks?.length) {
    marks = state.storedMarks;
  } else {
    marks = getNearestMarksFromSelection(state, tag);
  }

  // replace marks & attributes in copied text
  let newSlice = replaceMarks(slice, marks);
  // only replacing the lineHeight for now - can be extended to other attributes if needed
  if (lineHeight) newSlice = replaceLineHeight(newSlice, lineHeight);

  // paste copied text
  tr.replaceSelection(newSlice);

  dispatch(tr);

  return true;
};

/**
 * Wrap the editor content with a heading tag (from the CustomHeader extension)
 * (For paragraphs tags no wrapping is needed)
 *
 * Before:
 * <span>text</span>
 *
 * After:
 * <h1><span>text</span></h1>
 *
 * @param editor
 * @param textTag
 */
export const wrapTextWithTag = (editor: Editor, textTag: TextWidgetTag) => {
  const firstNodeName = editor!.view.state.doc.firstChild?.type.name;
  const isHeader = textTag !== 'paragraph';
  const isWrappedInParagraph = firstNodeName === 'paragraph';

  // should be wrapped in a header
  if (isWrappedInParagraph && isHeader) {
    // get the header level of the text tag
    const level: Level = parseInt(textTag.charAt(textTag.length - 1)) as 1 | 2 | 3 | 4 | 5 | 6;

    // Select all the content in the editor
    editor.view.dispatch(editor.state.tr.setSelection(new AllSelection(editor.state.doc)));
    // wrap all the text content with the selected header level
    editor.chain().setHeading().setHeadingLevel({ level }).run();
  }
};

/**
 * Find the first font size of the Editor content
 * @param editor
 * @returns font size as a string (defaults to 16px if no font size is found)
 */
export const findFirstFontSize = (editor: Editor | null) => {
  if (!editor) return '16px';

  const { doc } = editor.state;
  let firstFontSize: string | null = null;

  // iterate over all text nodes
  doc.nodesBetween(0, doc.content.size, (node) => {
    // if the node has the fontSize attribute, use it
    if (node.attrs.textStyle?.fontSize && !firstFontSize) firstFontSize = node.attrs.textStyle?.fontSize;

    // else get the font size from the marks
    const marks = node.marks;
    const markFontSize = marks.find(isTextStyleMark)?.attrs.fontSize;
    if (markFontSize && !firstFontSize) firstFontSize = markFontSize;
  });

  return firstFontSize || '16px';
};

/**
 * Find the first line height of the Editor content
 * @param editor
 * @returns line height as a string (defaults to 1.2 if no height is found)
 */
export const findFirstLineHeight = (editor: Editor | null) => {
  if (!editor) return DEFAULT_LINEHEIGHT;

  const { doc } = editor.state;
  let firstLineHeight: string | null = null;

  // iterate over all text nodes
  doc.nodesBetween(0, doc.content.size, (node) => {
    const attrs = node.attrs;
    if (attrs.lineHeight && !firstLineHeight) firstLineHeight = attrs.lineHeight;
  });

  return firstLineHeight || DEFAULT_LINEHEIGHT;
};

/**
 * Find the first color of the Editor content
 * @param editor
 * @returns color as a string
 */
export const findFirstColor = (editor: Editor | null) => {
  if (!editor) return '';

  const { doc } = editor.state;
  let firstFontColor: string | null = null;

  // iterate over all text nodes
  doc.nodesBetween(0, doc.content.size, (node) => {
    // else get the font size from the marks
    const marks = node.marks;
    const markColor = marks.find(isTextStyleMark)?.attrs.color;
    if (markColor && !firstFontColor) firstFontColor = markColor;
  });

  return firstFontColor || '';
};

/**
 * Format the letter spacing into em unit
 * @param letterSpacing number
 * @returns string
 */
export const formatLetterSpacing = (letterSpacing: number) => letterSpacing / 1000 + 'em';

/**
 * Replace text content of prose mirror editor while keeping all the same styling
 *
 * @param editor
 * @param text
 * @returns
 */
export const replaceTextContent = (editor: Editor, text: string) => {
  if (!editor) return;

  const nodeAttributes = getNodeAttributes(editor.state, editor.schema.nodes.paragraph);
  const lineHeight = findFirstLineHeight(editor);
  const textAlign = nodeAttributes?.textAlign;

  // Get the style for the first character in the widget
  const firstCharacterMarks = getFirstCharacterTextMarks(editor.getJSON());
  const customStyle = getFontStyleFromTextMarks(firstCharacterMarks);

  // If some styling is missing, use default paragraph styling
  const defaultWidgetData = DEFAULT_TEXT_WIDGET_DATA.paragraph;
  const { style: defaultStyle } = defaultWidgetData;
  const attrs = {
    lineHeight,
    textAlign,
    fontSize: customStyle?.fontSize || defaultStyle.fontSize + 'px',
  };

  const proseMirrorData: JSONContent = {
    type: 'doc',
    content: generatePMParagraphContent(text, firstCharacterMarks, attrs),
  };

  editor.commands.setContent(proseMirrorData);
};

/**
 * Compute the text bounding box height based on the width, text, font style and letter spacing
 *
 * @param widthPx - width of bb
 * @param text - text content
 * @param fontStyle - styling of text
 * @param letterSpacing
 * @returns number - height of the bb
 */
export const computeTextBoundingBoxHeight = (
  widthPx: number,
  text: string,
  fontStyle: FontStyleFromTextMarks,
  lineHeight: string,
  letterSpacing: number | undefined,
) => {
  let container;
  let heightPx = 0;
  try {
    container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.top = '-100%';
    container.style.left = '-100%';
    container.style.width = '0';
    container.style.height = '0';
    container.style.opacity = '0';

    const textEl = document.createElement('div');
    container.appendChild(textEl);
    document.body.appendChild(container);

    const { fontFamily, isBold, isItalic, fontSize } = fontStyle;
    Object.assign(textEl.style, {
      fontFamily,
      fontWeight: isBold ? 'bold' : 'normal',
      fontStyle: isItalic ? 'italic' : 'normal',
      fontSize,
      width: `${widthPx}px`,
      whiteSpace: 'pre-line',
      lineHeight,
      letterSpacing: letterSpacing ? formatLetterSpacing(letterSpacing) : '0',
    });

    textEl.textContent = text;
    heightPx = textEl.getBoundingClientRect()?.height ?? 0;
  } catch (error) {
    console.error(error);
  } finally {
    container?.remove();
  }
  return heightPx;
};

/**
 * Checks if the link is active in the selected widget.
 *
 * @param editor - The editor instance.
 * @param isEditable
 *
 * @returns True if the link is active, false otherwise.
 */
export const isLinkActive = (editor: Editor, isEditable: boolean) => {
  if (!editor) return false;
  if (isEditable) return editor.isActive('link');

  // if the widget is activ => check the entire editor doc for links
  // the link mark is not extended when you continue typing
  // like the bold/underline/italic marks so we have to check the entire doc
  const from = 1;
  const to = editor.state.doc.content.size;
  const links = getMarksBetween(from, to, editor.state.doc).filter(
    (markRange) => markRange.mark?.type?.name === 'link',
  );

  if (links.length === 1) {
    const { from: linkMarkFrom, to: linkMarkTo } = links[0];

    // links won't be extended while typing so the mark will be exactly from -> to - 1
    if (linkMarkFrom === from && linkMarkTo === to - 1) {
      return true;
    }
  }

  return false;
};

/**
 * Checks if the editor contains a bullet list or an ordered list
 * (active will only check if the entire editor is a list)
 *
 * @param editor
 * @returns boolean
 */
export const containsList = (editor: Editor) => {
  // check if a bulletList node or a orderedList node is present in the editor
  let hasList = false;

  editor.state.doc.descendants((node) => {
    if (node.type.name === 'bulletList' || node.type.name === 'orderedList') {
      hasList = true;
      return;
    }
  });

  return hasList;
};
